"use client";

import { Box, Flex, Icon } from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import { HiOutlineChevronRight } from "react-icons/hi2";
import type { GetLocationResponse } from "@/openapi/generated";
import { EntryType, MediaGroup } from "@/openapi/generated/models";
import {
  usePresignedImageErrorRecovery,
  usePublicListingMediaUrls,
} from "@/features/media/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useTenantData } from "@/hooks";
import { formatLocationAddressSingleLine } from "@/lib/utils/formatLocationAddress";
import { resolveLocationCoverUrl } from "@/lib/utils/media/fetch-media-asset";
import { invalidateMediaAssetQueries } from "@/lib/utils/media/media-asset-query-key";
import { buildLocationPublicPath, appendFromListingQuery } from "@/lib/utils/publicPaths";
import { getLocationOptionalDescription } from "@/lib/utils/locationPublic";
import { useThemeTokens } from "@/lib/theme/tokens";
import { ImageWithFallback } from "@/components/shared";
import { Link, Text } from "@/components/ui";

export interface LocationsListProps {
  data: GetLocationResponse[];
}

interface LocationListItemProps {
  location: GetLocationResponse;
  href: string;
  imageUrl: string | null;
  isLast: boolean;
}

const LocationListItem = ({
  location,
  href,
  imageUrl,
  isLast,
}: LocationListItemProps) => {
  const theme = useThemeTokens();
  const queryClient = useQueryClient();
  const { tenantId } = useTenantData();
  const locationId = Number(location.locationId);
  const coverImageUrl = resolveLocationCoverUrl(imageUrl);
  const addressLine = formatLocationAddressSingleLine(location);
  const descriptionPreview = getLocationOptionalDescription(location);

  const refreshImage = useCallback(
    () =>
      invalidateMediaAssetQueries(
        queryClient,
        tenantId,
        EntryType.Location,
        locationId,
        MediaGroup.Cover,
      ),
    [queryClient, tenantId, locationId],
  );
  const handleCoverImageError = usePresignedImageErrorRecovery(
    coverImageUrl,
    refreshImage,
  );

  return (
    <Link
      href={href}
      display="block"
      py={{ base: 5, md: 6 }}
      borderBottomWidth={isLast ? 0 : "1px"}
      borderColor={theme.borderGlass.subtle}
      outline="none"
      _hover={{ textDecoration: "none" }}
      _focusVisible={{
        boxShadow: theme.focusRing,
        borderRadius: "8px",
      }}
      aria-label={`View details for ${location.name || "location"}`}
      sx={{
        "@media (hover: hover)": {
          "&:hover [data-location-name]": { color: theme.accent.primary },
        },
      }}
    >
      <Flex
        direction={{ base: "column", lg: "row" }}
        align={{ base: "stretch", lg: "flex-start" }}
        gap={{ base: 4, lg: 8 }}
      >
        <Box
          flexShrink={0}
          w={{ base: "100%", lg: "148px" }}
          maxW={{ base: "220px", lg: "148px" }}
          borderRadius="10px"
          overflow="hidden"
          borderWidth="1px"
          borderColor={theme.borderGlass.default}
          position="relative"
          aspectRatio="16 / 10"
        >
          {!coverImageUrl ? (
            <Box
              position="absolute"
              inset={0}
              bg={`linear-gradient(135deg, ${theme.accent.primary} 0%, ${theme.accent.mutedHex || theme.accent.muted} 100%)`}
            />
          ) : null}
          <Box position="relative" h="100%" w="100%">
            <ImageWithFallback
              src={coverImageUrl}
              alt={location.name || "Location"}
              fallbackType="image"
              width="100%"
              height="100%"
              borderRadius="0"
              objectFit="cover"
              onImageError={handleCoverImageError}
            />
          </Box>
        </Box>

        <Flex direction="column" flex={1} minW={0} gap={2}>
          <Text
            as="h2"
            data-location-name
            variant="primary"
            fontSize={{ base: "lg", md: "xl" }}
            fontWeight="600"
            letterSpacing="-0.02em"
            lineHeight="1.25"
            transition="color 0.2s ease"
            noOfLines={2}
          >
            {location.name}
          </Text>

          {addressLine ? (
            <Text variant="secondary" fontSize="sm" lineHeight="1.6" noOfLines={2}>
              {addressLine}
            </Text>
          ) : null}

          {descriptionPreview ? (
            <Text
              variant="tertiary"
              fontSize="sm"
              lineHeight="1.6"
              noOfLines={{ base: 3, md: 2 }}
            >
              {descriptionPreview}
            </Text>
          ) : null}
        </Flex>

        <Flex
          flexShrink={0}
          align={{ base: "flex-end", lg: "center" }}
          justify="flex-end"
          minW={{ lg: "100px" }}
          pt={{ lg: 2 }}
          color={theme.text.tertiary}
          fontSize="sm"
          fontWeight="500"
          gap={1}
        >
          <Text as="span">Details</Text>
          <Icon as={HiOutlineChevronRight} boxSize="16px" aria-hidden />
        </Flex>
      </Flex>
    </Link>
  );
};

export const LocationsList = ({ data }: LocationsListProps) => {
  const theme = useThemeTokens();
  const ownerIds = useMemo(
    () =>
      data.map((location) => Number(location.locationId)).filter((id) => id > 0),
    [data],
  );
  const imageUrls = usePublicListingMediaUrls({
    ownerType: EntryType.Location,
    ownerIds,
    mediaGroup: MediaGroup.Cover,
  });

  return (
    <Box
      as="ul"
      listStyleType="none"
      m={0}
      p={0}
      borderTopWidth="1px"
      borderColor={theme.borderGlass.subtle}
    >
      {data.map((location, index) => {
        const locationId = Number(location.locationId);
        const href = appendFromListingQuery(
          buildLocationPublicPath({
            name: location.name || "location",
            locationId,
          }),
          "locations",
        );

        return (
          <Box as="li" key={String(location.locationId)} listStyleType="none">
            <LocationListItem
              location={location}
              href={href}
              imageUrl={imageUrls[locationId] ?? null}
              isLast={index === data.length - 1}
            />
          </Box>
        );
      })}
    </Box>
  );
};
